// Writes <mission_workspace>/.claude/state/autopilot-binding.json atomically
// (tmp -> rename), then chmod 0444 AFTER rename.
//
// `writeJsonAtomic` in filesystem-helpers.ts chmods the TMP file BEFORE
// rename (its own doc comment says so). Chmod-before-rename is functionally
// fine on POSIX (permissions travel with rename, and rename onto an existing
// 0444 destination is not blocked by perms) — but this writer's contract is
// explicitly "chmod 0444 AFTER the file is in its final location" per the
// phase spec, so we replicate the tmp+rename pattern locally with chmod
// applied post-rename instead of reusing writeJsonAtomic's pre-rename chmod.
// This keeps the atomicity guarantee (readers never observe a partial file)
// while making the mutation-after-rename ordering explicit and testable.
import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { dirname, join } from "node:path";
import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";
import type { RuntimeLimits } from "./runtime-limits-from-effective-config.js";

export interface BindingWriterInput {
  missionId: string;
  ticketId: string;
  runId: string;
  sessionId: string;
  repoRoot: string;
  allowedPaths: string[];
  allowedTools: string[];
  kind: string;
}

export interface BindingWriteResult {
  bindingPath: string;
  checksum: string;
}

const BINDING_SCHEMA_VERSION = 1;
const BINDING_FILE_MODE = 0o444;

// expires_at = t0 + timeouts_min[kind]*60_000 + shutdown_grace_sec*1000 +
// lease_stale_sec*1000 (D2). `t0` must be passed in explicitly — this
// function must NOT call Date.now()/deps.now() itself (runner owns the
// single spawn_at moment, see session-runner.ts).
function computeExpiresAtIso(t0: number, limits: RuntimeLimits): string {
  const expiresAtMs = t0 + limits.deadlineMs + limits.graceMs + limits.leaseStaleSec * 1000;
  return new Date(expiresAtMs).toISOString();
}

/** Absolute binding path — shared with the worker prompt (P-1) so both name one file. */
export function autopilotBindingFilePath(missionWorkspace: string): string {
  return join(missionWorkspace, ".claude", "state", "autopilot-binding.json");
}

export async function writeAutopilotBindingFile(
  missionWorkspace: string,
  input: BindingWriterInput,
  t0: number,
  limits: RuntimeLimits,
): Promise<BindingWriteResult> {
  const bindingPath = autopilotBindingFilePath(missionWorkspace);
  const issuedAtIso = new Date(t0).toISOString();
  const expiresAtIso = computeExpiresAtIso(t0, limits);

  const bodyWithoutChecksum = {
    schema_version: BINDING_SCHEMA_VERSION,
    mission_id: input.missionId,
    ticket_id: input.ticketId,
    run_id: input.runId,
    session_id: input.sessionId,
    issued_at: issuedAtIso,
    expires_at: expiresAtIso,
    repo_root: input.repoRoot,
    allowed_paths: input.allowedPaths,
    allowed_tools: input.allowedTools,
  };
  const checksum = sha256Checksum(canonicalJson(bodyWithoutChecksum));
  const fullBody = { ...bodyWithoutChecksum, checksum };

  await fs.mkdir(dirname(bindingPath), { recursive: true });
  const tmpPath = `${bindingPath}.tmp-${process.pid}-${randomUUID()}`;
  let renamed = false;
  try {
    await fs.writeFile(tmpPath, `${JSON.stringify(fullBody, null, 2)}\n`, "utf8");
    await fs.rename(tmpPath, bindingPath);
    renamed = true;
    await fs.chmod(bindingPath, BINDING_FILE_MODE);
  } finally {
    if (!renamed) await fs.rm(tmpPath, { force: true }).catch(() => {});
  }

  return { bindingPath, checksum };
}
